import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { Radio, ExternalLink, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60000;
const DISMISS_KEY = 'tb-live-dismissed';

const useLiveStatus = () => {
  const [live, setLive] = useState(null);
  useEffect(() => {
    const check = () => axios.get(`${API}/media/live`)
      .then((res) => setLive(res.data.is_live ? res.data : null))
      .catch(() => {});
    check();
    const id = setInterval(check, POLL_MS);
    return () => clearInterval(id);
  }, []);
  return live;
};

export const LiveBanner = () => {
  const live = useLiveStatus();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY));
  const visible = live && dismissed !== live.video_id;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, live.video_id);
    setDismissed(live.video_id);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed top-20 left-0 right-0 z-40 bg-red-700 border-b border-gold/40 shadow-lg"
          data-testid="live-banner"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3 sm:gap-4">
            <span className="relative flex h-3 w-3 flex-shrink-0">
              <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-white" />
            </span>
            <p className="flex-1 min-w-0 text-white text-sm">
              <span className="font-bold uppercase tracking-wider mr-2">We're Live</span>
              <span className="hidden md:inline text-white/85 truncate" data-testid="live-banner-title">{live.title || 'Join the service now'}</span>
            </p>
            <Link to="/media" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-1.5 bg-gold text-black text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm hover:bg-white transition-colors" data-testid="live-banner-watch-here">
              <Radio size={14} /> Watch Here
            </Link>
            <a href={`https://www.youtube.com/watch?v=${live.video_id}`} target="_blank" rel="noopener noreferrer" aria-label="Open live stream on YouTube" className="inline-flex items-center gap-1.5 border border-white/70 text-white text-xs font-bold uppercase tracking-wider px-2 sm:px-3 py-1.5 rounded-sm hover:bg-white hover:text-red-700 transition-colors" data-testid="live-banner-youtube">
              <ExternalLink size={14} /> <span className="hidden sm:inline">Open on YouTube</span>
            </a>
            <button onClick={dismiss} aria-label="Dismiss live banner" className="text-white/80 hover:text-white transition-colors p-1" data-testid="live-banner-dismiss">
              <X size={18} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
